import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getUser, updateUser } from '../api/users.ts';
import type { UserEntity, UserCalendarAvailabilitySlotEntity } from '../api/entity_types/User.ts';
import { Api } from '@/api/index.ts';

type UserCalendar = {
  isGoogleAuthenticated: boolean;
  isMicrosoftAuthenticated: boolean;
};

type DayAvailability = {
  isAvailable: boolean;
  startTime: string;
  endTime: string;
};

type UserAvailability = {
  sunday: DayAvailability;
  monday: DayAvailability;
  tuesday: DayAvailability;
  wednesday: DayAvailability;
  thursday: DayAvailability;
  friday: DayAvailability;
  saturday: DayAvailability;
};

type NotificationSettings = {
  notifyOnNewMessage: boolean;
  notifyOnNewApplication: boolean;
  notifyDailyPipelineReport: boolean;
  notifyWeeklyDigestReport: boolean;
  notifyOnEventScheduled: boolean;
  notifyOnEventReminderEmail: boolean;
  notifyOnEventReminderSMS: boolean;
  notifyOnStaleJobs: boolean;
};

const defaultUserCalendar: UserCalendar = {
  isGoogleAuthenticated: false,
  isMicrosoftAuthenticated: false,
};

const defaultDayAvailability: DayAvailability = {
  isAvailable: false,
  startTime: '',
  endTime: '',
};

const defaultUserAvailability: UserAvailability = {
  sunday: { ...defaultDayAvailability },
  monday: { ...defaultDayAvailability },
  tuesday: { ...defaultDayAvailability },
  wednesday: { ...defaultDayAvailability },
  thursday: { ...defaultDayAvailability },
  friday: { ...defaultDayAvailability },
  saturday: { ...defaultDayAvailability },
};

const defaultNotificationSettings: NotificationSettings = {
  notifyOnNewMessage: false,
  notifyOnNewApplication: false,
  notifyDailyPipelineReport: false,
  notifyWeeklyDigestReport: false,
  notifyOnEventScheduled: false,
  notifyOnEventReminderEmail: false,
  notifyOnEventReminderSMS: false,
  notifyOnStaleJobs: false,
};

export const useUserCalendarStore = defineStore('userCalendar', () => {
  const currentUserCalendar = ref<UserCalendar>({ ...defaultUserCalendar });
  const currentUserAvailability = ref<UserAvailability>({ ...defaultUserAvailability });
  const currentUserNotificationSettings = ref<NotificationSettings>({ ...defaultNotificationSettings });

  const loadUserCalendarData = async () => {
    try {
      const data = await getUser();

      if (data.user_calendar_availability) {
        setCurrentUserAvailability({ availability: data.user_calendar_availability });
      }

      if (data.record) {
        setCurrentUserNotificationSettings({ notificationSettings: data.record });
      }
    } catch (error) {
      console.error('Failed to load user calendar data:', error);
    }
  };

  const loadCalendarAuthStatus = async (user: { id: string }) => {
    try {
      const response = await Api.CalendarConnections.getCalendarConnection(Number(user.id));
      if (response.status === 'success') {
        setCurrentUserCalendar({
          isGoogleAuthenticated: response.data.is_google_authenticated,
          isMicrosoftAuthenticated: response.data.is_microsoft_authenticated,
        });
        return response.data;
      }
    } catch (error) {
      console.error('Failed to load calendar auth status:', error);
      throw error;
    }
  };

  const updateUserNotifications = async (user: { id: string }, settings: NotificationSettings) => {
    try {
      const updatedUserAttributes = {
        notify_on_new_message: settings.notifyOnNewMessage,
        notify_on_new_application: settings.notifyOnNewApplication,
        notify_daily_pipeline_report: settings.notifyDailyPipelineReport,
        notify_weekly_digest_report: settings.notifyWeeklyDigestReport,
        notify_on_event_scheduled: settings.notifyOnEventScheduled,
        notify_on_event_reminder_email: settings.notifyOnEventReminderEmail,
        notify_on_event_reminder_sms: settings.notifyOnEventReminderSMS,
        notify_on_stale_jobs: settings.notifyOnStaleJobs,
      };

      await updateUser(user, { updatedUserAttributes });

      setCurrentUserNotificationSettings({ notificationSettings: settings });
    } catch (error) {
      console.error('Failed to update user notifications:', error);
      throw error;
    }
  };

  const setCurrentUserCalendar = ({ isGoogleAuthenticated, isMicrosoftAuthenticated }: UserCalendar) => {
    currentUserCalendar.value.isGoogleAuthenticated = isGoogleAuthenticated;
    currentUserCalendar.value.isMicrosoftAuthenticated = isMicrosoftAuthenticated;
  };

  const setCurrentUserAvailability = ({ availability }: { availability: UserCalendarAvailabilitySlotEntity[] }) => {
    for (let slot of availability) {
      const day = slot.day_of_week as keyof UserAvailability;
      if (currentUserAvailability.value[day]) {
        currentUserAvailability.value[day].startTime = slot.start_time;
        currentUserAvailability.value[day].endTime = slot.end_time;
        currentUserAvailability.value[day].isAvailable = slot.is_available;
      }
    }
  };

  const setCurrentUserNotificationSettings = ({
    notificationSettings,
  }: {
    notificationSettings: UserEntity | NotificationSettings;
  }) => {
    // Handle both UserEntity (from API with snake_case) and NotificationSettings (internal camelCase)
    if ('notifyOnNewMessage' in notificationSettings) {
      // Already in camelCase format
      Object.assign(currentUserNotificationSettings.value, notificationSettings);
    } else {
      // Convert from snake_case API response to camelCase
      currentUserNotificationSettings.value.notifyOnNewMessage = notificationSettings.notify_on_new_message ?? false;
      currentUserNotificationSettings.value.notifyOnNewApplication =
        notificationSettings.notify_on_new_application ?? false;
      currentUserNotificationSettings.value.notifyDailyPipelineReport =
        notificationSettings.notify_daily_pipeline_report ?? false;
      currentUserNotificationSettings.value.notifyWeeklyDigestReport =
        notificationSettings.notify_weekly_digest_report ?? false;
      currentUserNotificationSettings.value.notifyOnEventScheduled =
        notificationSettings.notify_on_event_scheduled ?? false;
      currentUserNotificationSettings.value.notifyOnEventReminderEmail =
        notificationSettings.notify_on_event_reminder_email ?? false;
      currentUserNotificationSettings.value.notifyOnEventReminderSMS =
        notificationSettings.notify_on_event_reminder_sms ?? false;
      currentUserNotificationSettings.value.notifyOnStaleJobs = notificationSettings.notify_on_stale_jobs ?? false;
    }
  };

  return {
    currentUserCalendar,
    currentUserAvailability,
    currentUserNotificationSettings,

    loadUserCalendarData,
    loadCalendarAuthStatus,
    updateUserNotifications,

    setCurrentUserCalendar,
    setCurrentUserAvailability,
    setCurrentUserNotificationSettings,
  };
});
